import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Header from "../../components/Header";
import { NavBar } from "../../components/NavBar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import { getActividadDocentesList } from "../../services/usuarios.service";
import { defer } from "rxjs";
import {
  startOfMonth,
  addMonths,
  startOfToday,
  endOfMonth,
  isValid,
  format,
  parseISO,
  differenceInMonths,
} from "date-fns";
import { Button } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import ClearIcon from "@mui/icons-material/Clear";

const InfoActividad = ({ actividad }) => {
  return (
    <Box sx={{ margin: 1 }}>
      <Typography variant="h6" gutterBottom component="div">
        Impartidos:
      </Typography>
      <Box
        sx={{
          padding: 1,
          border: "1px solid rgba(224, 224, 224, 1)",
          borderRadius: 1,
        }}
      >
        <ul>
          {actividad?.map((act) => (
            <li key={`${act.modulo}-${act.edicion}`}>
              <span>
                <strong>{act.edicion}</strong>{" "}
                {`(${act.modulo}) ${act.fechaFin}`}
              </span>
            </li>
          ))}
        </ul>
      </Box>
    </Box>
  );
};

const Row = ({ row, selected, onSelectRow }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <Checkbox
            checked={selected}
            onChange={(e) => onSelectRow(row.email, e.target.checked)}
          />
        </TableCell>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {/* <TableCell component="th" scope="row">
          <Avatar alt="Foto de perfil" src={row.profileURL} />
        </TableCell> */}
        <TableCell>
          <Tooltip title={row.nombre}>
            <strong>{row.nombre}</strong>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={row.email}>
            <span>{row.email}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="center">{row.impartidos?.length ?? 0}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <InfoActividad actividad={row.impartidos} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const DocentesTable = ({
  rows,
  selectedRows,
  onSelectAll,
  onSelectRow,
  isLoading,
}) => {
  const allSelected = rows.length > 0 && selectedRows.size === rows.length;
  const indeterminate =
    selectedRows.size > 0 && selectedRows.size < rows.length;

  return (
    <TableContainer component={Paper}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell>
              <Checkbox
                checked={allSelected}
                indeterminate={indeterminate}
                onChange={(e) => onSelectAll(e.target.checked)}
                disabled={isLoading}
              />
            </TableCell>
            <TableCell />
            {/* <TableCell>Foto</TableCell> */}
            <TableCell>Nombre</TableCell>
            <TableCell>Correo</TableCell>
            <TableCell align="center">Cantidad</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Cargando docentes...
                </Typography>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No se encontraron docentes en el rango de fechas seleccionado
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((edicion) => (
              <Row
                key={edicion.nombre}
                row={edicion}
                selected={selectedRows.has(edicion.email)}
                onSelectRow={onSelectRow}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const Filtro = ({
  isLoading,
  desde,
  hasta,
  docenteNombre,
  setDesde,
  setHasta,
  setDocenteNombre,
}) => {
  const [desdeLocal, setDesdeLocal] = useState(desde);
  const [hastaLocal, setHastaLocal] = useState(hasta);
  const [docenteNombreLocal, setDocenteNombreLocal] = useState(docenteNombre);
  const [inputError, setInputError] = useState({});

  const validate = () => {
    let tempError = {};
    const desdeDate = parseISO(desdeLocal);
    const hastaDate = parseISO(hastaLocal);

    if (!isValid(desdeDate)) {
      tempError = { ...tempError, desde: "Seleccione una fecha valida" };
    }
    if (!isValid(hastaDate)) {
      tempError = { ...tempError, hasta: "Seleccione una fecha valida" };
    }

    if (isValid(desdeDate) && isValid(hastaDate)) {
      if (hastaDate < desdeDate) {
        tempError = {
          ...tempError,
          hasta: "La fecha 'hasta' debe ser posterior a 'desde'",
        };
      }

      const monthsDiff = differenceInMonths(hastaDate, desdeDate);
      if (monthsDiff > 6) {
        tempError = {
          ...tempError,
          hasta: "El rango máximo permitido es de 6 meses",
        };
      }
    }

    setInputError(tempError);
    return Object.values(tempError).every((e) => !e);
  };

  return (
    <Paper
      sx={{
        padding: "8px;",
        margin: "8px 0px 8px 0px;",
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <TextField
            id="desde"
            label="Desde"
            name="desde"
            fullWidth
            size="small"
            value={desdeLocal}
            onChange={(e) => setDesdeLocal(e.target.value)}
            type="date"
            disabled={isLoading}
            error={!!inputError["desde"]}
            helperText={inputError["desde"]}
            required
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            id="hasta"
            label="Hasta"
            name="hasta"
            fullWidth
            size="small"
            value={hastaLocal}
            onChange={(e) => setHastaLocal(e.target.value)}
            type="date"
            disabled={isLoading}
            error={!!inputError["hasta"]}
            helperText={inputError["hasta"]}
            required
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            id="docenteNombre"
            label="Docente"
            name="docenteNombre"
            fullWidth
            size="small"
            value={docenteNombreLocal}
            onChange={(e) => setDocenteNombreLocal(e.target.value)}
            type="text"
            disabled={isLoading}
            placeholder="Nombre del docente..."
            InputProps={{
              endAdornment: docenteNombreLocal && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDocenteNombreLocal("");
                      setDocenteNombre("");
                    }}
                    disabled={isLoading}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              if (validate()) {
                setDesde(desdeLocal);
                setHasta(hastaLocal);
                setDocenteNombre(docenteNombreLocal);
              }
            }}
            disabled={isLoading}
          >
            Filtrar
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

const aMonthAgo = addMonths(startOfToday(), -1);

// Función para generar CSV
const generateCSV = (selectedData) => {
  const headers = "Nombre;Email;Modulo;Edicion;Fecha\n";
  const rows = selectedData
    .map((item) => {
      return (
        item.impartidos
          ?.map(
            (actividad) =>
              `"${item.nombre}";"${item.email}";"${actividad.modulo}";"${actividad.edicion}";"${actividad.fechaFin}"`
          )
          .join("\n") || `"${item.nombre}";"${item.email}";"";"";""`
      );
    })
    .join("\n");

  return headers + rows;
};

// Función para descargar CSV
const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ActividadDocentes = () => {
  const [isLoading, setLoading] = useState(true);
  const [desde, setDesde] = useState(
    format(startOfMonth(aMonthAgo), "yyyy-MM-dd")
  );
  const [hasta, setHasta] = useState(
    format(endOfMonth(aMonthAgo), "yyyy-MM-dd")
  );
  const [docenteNombre, setDocenteNombre] = useState("");
  const [itemsList, setItemsList] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Manejar selección individual
  const handleSelectRow = (email, isSelected) => {
    const newSelected = new Set(selectedRows);
    if (isSelected) {
      newSelected.add(email);
    } else {
      newSelected.delete(email);
    }
    setSelectedRows(newSelected);
  };

  // Manejar selección de todos
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedRows(new Set(itemsList.map((item) => item.email)));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Exportar a CSV
  const handleExportCSV = () => {
    const selectedData = itemsList.filter((item) =>
      selectedRows.has(item.email)
    );
    if (selectedData.length === 0) {
      alert("Por favor selecciona al menos un docente para exportar");
      return;
    }

    const csvContent = generateCSV(selectedData);
    const filename = `actividad_docentes_${desde}_${hasta}.csv`;
    downloadCSV(csvContent, filename);
  };

  useEffect(() => {
    setLoading(true);
    setSelectedRows(new Set());
    const subscription = defer(() =>
      getActividadDocentesList(parseISO(desde), parseISO(hasta), docenteNombre)
    ).subscribe((list) => {
      setItemsList(list);
      setLoading(false);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [desde, hasta, docenteNombre]);

  return (
    <Box sx={{ display: "flex", mt: 4, mb: 4 }}>
      <NavBar selected="Actividad Docentes" />
      <Container maxWidth="lg">
        <Header
          title="Activida Docentes"
          subtitle="Listado de docentes con actividad"
        />
        <Filtro
          isLoading={isLoading}
          desde={desde}
          hasta={hasta}
          docenteNombre={docenteNombre}
          setDesde={setDesde}
          setHasta={setHasta}
          setDocenteNombre={setDocenteNombre}
        />

        {/* Botón de exportar */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
          }}
        >
          {docenteNombre && (
            <Typography variant="body2" color="text.secondary">
              Filtrando por: "{docenteNombre}" ({itemsList.length} resultados)
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportCSV}
            disabled={isLoading || selectedRows.size === 0}
            sx={{ mr: 1 }}
          >
            Exportar CSV ({selectedRows.size} seleccionados)
          </Button>
        </Box>

        <DocentesTable
          rows={itemsList}
          selectedRows={selectedRows}
          onSelectAll={handleSelectAll}
          onSelectRow={handleSelectRow}
          isLoading={isLoading}
        />
      </Container>
    </Box>
  );
};
